import { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { X, Lightbulb, ArrowRight, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ideaApi, workItemApi } from '../../services/api';
import { useProjectStore } from '../../store/useProjectStore';
import { usePIStore } from '../../store/usePIStore';
import { useOrgHierarchyStore } from '../../store/useOrgHierarchyStore';
import { useAuthStore } from '../../store/useAuthStore';
import { IDEA_STATUS_META } from '../../types';
import type { IdeaStatus, WorkItem } from '../../types';

interface Props {
  idea: WorkItem;
  onClose: () => void;
  onChanged: (idea: WorkItem) => void;
}

// Which manual transitions the pipeline offers from each state.
const NEXT_STATES: Record<IdeaStatus, IdeaStatus[]> = {
  draft: ['submitted', 'parked'],
  submitted: ['in_review', 'parked', 'rejected'],
  in_review: ['parked', 'rejected'],
  approved: [],
  rejected: ['submitted'],
  parked: ['submitted'],
  realized: [],
};

export default function IdeaDetailModal({ idea, onClose, onChanged }: Props) {
  const navigate = useNavigate();
  const { projects } = useProjectStore();
  const { pis, getSprintsForPI, fetchPIs } = usePIStore();
  const { organizations, programs, fetchOrganizations, fetchPrograms } = useOrgHierarchyStore();
  const { hasRole } = useAuthStore();
  const canApprove = hasRole('super_admin', 'project_admin');

  const isProgram = idea.ideaLevel === 'program';            // a new training programme (EP-26 FE-9)
  const isProject = idea.ideaLevel === 'project' || isProgram;   // both live under an org, not a project
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState(false);
  const [epics, setEpics] = useState<WorkItem[]>([]);
  const cf = (idea.customFields || {}) as any;
  const [approve, setApprove] = useState({
    targetPiId: '',
    sprintId: '',
    parentEpicId: (cf.parentEpicHint as string) || (idea.parentId as string) || '',
    createParentEpic: false,
    newEpicTitle: '',
    organizationId: (idea.targetOrgId as string) || '',
    programId: (idea.targetProgramId as string) || '',
  });

  const hyp = cf.hypothesis || {};
  const project = projects.find((p) => p._id === idea.projectId);
  const meta = IDEA_STATUS_META[(idea.ideaStatus || 'draft') as IdeaStatus];
  const isFeature = idea.ideaLevel === 'feature';

  useEffect(() => {
    if (pis.length === 0) fetchPIs();
    if (isProject) {
      if (organizations.length === 0) fetchOrganizations();
      if (programs.length === 0) fetchPrograms();
    }
  }, [pis.length, fetchPIs, isProject, organizations.length, programs.length, fetchOrganizations, fetchPrograms]);

  useEffect(() => {
    if (approving && isFeature && idea.projectId) {
      workItemApi.list({ projectId: idea.projectId, workItemType: 'epic' })
        .then((r) => setEpics(r || []))
        .catch(() => setEpics([]));
    }
  }, [approving, isFeature, idea.projectId]);

  const sprints = useMemo(
    () => (approve.targetPiId ? getSprintsForPI(approve.targetPiId) : []),
    [approve.targetPiId, getSprintsForPI]
  );
  const orgPrograms = useMemo(
    () => programs.filter((p) => p.organizationId === approve.organizationId),
    [programs, approve.organizationId]
  );

  const transition = async (to: IdeaStatus) => {
    setBusy(true);
    try {
      const updated = await ideaApi.setStatus(idea._id, to);
      toast.success(`Moved to ${IDEA_STATUS_META[to].label}`);
      onChanged(updated);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  const doApprove = async () => {
    if (isFeature && !approve.parentEpicId && !approve.createParentEpic) {
      toast.error('Feature-level ideas need a parent epic — pick one or create a new epic.');
      return;
    }
    if (isProject && !approve.organizationId) {
      toast.error(isProgram ? 'Choose an organization for the new programme.' : 'Choose an organization for the new project.');
      return;
    }
    setBusy(true);
    try {
      const res = await ideaApi.approve(idea._id, {
        targetPiId: approve.targetPiId,
        sprintId: approve.sprintId,
        parentEpicId: isFeature ? approve.parentEpicId : '',
        createParentEpic: isFeature ? approve.createParentEpic : false,
        newEpicTitle: approve.newEpicTitle,
        organizationId: isProject ? approve.organizationId : '',
        programId: isProject && !isProgram ? approve.programId : '',
      });
      toast.success(isProgram ? 'Idea approved — training programme created' : isProject ? 'Idea approved — new project created' : 'Idea approved — delivery item created');
      onChanged(res.idea);
      setApproving(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to approve idea');
    } finally {
      setBusy(false);
    }
  };

  const input =
    'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-accent outline-none';
  const label = 'block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1';

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-2xl bg-white dark:bg-gray-900 rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <Lightbulb className="text-amber-500 mt-0.5" size={20} />
              <div>
                <Dialog.Title className="text-base font-semibold">{idea.title}</Dialog.Title>
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {idea.displayId && <span className="font-mono">{idea.displayId}</span>}
                  <span>·</span>
                  <span>{isProject
                    ? (organizations.find((o) => o._id === idea.targetOrgId)?.name || 'New project')
                    : (project?.name || 'No project')}</span>
                  <span>·</span>
                  <span className="capitalize">{idea.ideaLevel || 'epic'} level</span>
                  <span className={`px-2 py-0.5 rounded-full ${meta.badge}`}>{meta.label}</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
            {idea.description && <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{idea.description}</p>}

            {(hyp.forWhom || hyp.theSolution) && (
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-4 py-3">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Hypothesis</div>
                <p className="text-gray-700 dark:text-gray-200">
                  For <b>{hyp.forWhom || '…'}</b> who {hyp.theProblem || '…'}, the <b>{hyp.theSolution || '…'}</b> delivers {hyp.theBenefit || '…'}
                  {hyp.unlike ? <> — unlike {hyp.unlike}.</> : '.'}
                </p>
              </div>
            )}

            {(cf.successMetrics || cf.leadingIndicators) && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Leading indicators</div>
                  <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{cf.leadingIndicators || '—'}</p>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Success metrics</div>
                  <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{cf.successMetrics || '—'}</p>
                </div>
              </div>
            )}

            <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>WSJF <b className="text-accent">{(idea.wsjfScore || 0).toFixed(2)}</b></span>
              <span>BV {idea.businessValue}</span>
              <span>TC {idea.timeCriticality}</span>
              <span>RR {idea.riskReductionOpportunity}</span>
              <span>Size {idea.jobSize}</span>
              {idea.proposedBy && <span>· proposed by {idea.proposedBy}</span>}
            </div>

            {/* Realized link */}
            {idea.ideaStatus === 'realized' && idea.realizedItemId && (
              <div className="rounded-lg bg-violet-50 dark:bg-violet-900/20 px-4 py-3 flex items-center justify-between">
                <span className="text-violet-700 dark:text-violet-300">
                  This idea has been realized as a {isProgram ? 'training programme' : isProject ? 'new project' : 'delivery item'}.
                </span>
                <button
                  onClick={() => { onClose(); navigate(isProgram ? `/training/${idea.realizedItemId}` : isProject ? `/project/${idea.realizedItemId}` : `/item/${idea.realizedItemId}`); }}
                  className="flex items-center gap-1 text-violet-700 dark:text-violet-300 font-semibold hover:underline"
                >
                  Open <ExternalLink size={14} />
                </button>
              </div>
            )}

            {/* Approve panel */}
            {approving && (
              <div className="rounded-lg border border-accent/40 bg-accent/5 px-4 py-4 space-y-3">
                <div className="font-semibold text-accent flex items-center gap-1"><ArrowRight size={16} /> Approve & schedule</div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isProgram
                    ? <>Approving creates a <b>training programme</b> under the chosen organization: its training project, three empty duty groups and a task to provision its stores. You need <code>program:manage</code> there.</>
                    : isProject
                    ? <>Approving creates a <b>new project</b> under the chosen organization and program, and keeps this idea as a linked record.</>
                    : <>Approving spawns a new {idea.ideaLevel || 'epic'} under <b>{project?.name}</b> and keeps this idea as a linked record.</>}
                </p>

                {/* Project-level: choose org + program for the new project */}
                {isProject && (
                  <>
                    <div>
                      <label className={label}>Organization *</label>
                      <select className={input} value={approve.organizationId} onChange={(e) => setApprove((a) => ({ ...a, organizationId: e.target.value, programId: '' }))}>
                        <option value="">Select an organization…</option>
                        {organizations.map((o) => (
                          <option key={o._id} value={o._id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                    {!isProgram && <div>
                      <label className={label}>Program (optional)</label>
                      <select className={input} value={approve.programId} disabled={!approve.organizationId} onChange={(e) => setApprove((a) => ({ ...a, programId: e.target.value }))}>
                        <option value="">No program</option>
                        {orgPrograms.map((p) => (
                          <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                      </select>
                    </div>}
                  </>
                )}

                <div>
                  <label className={label}>Program Increment</label>
                  <select className={input} value={approve.targetPiId} onChange={(e) => setApprove((a) => ({ ...a, targetPiId: e.target.value, sprintId: '' }))}>
                    <option value="">Schedule later</option>
                    {pis.map((pi) => (
                      <option key={pi._id} value={pi._id}>{pi.name}</option>
                    ))}
                  </select>
                </div>
                {approve.targetPiId && sprints.length > 0 && (
                  <div>
                    <label className={label}>Sprint (optional)</label>
                    <select className={input} value={approve.sprintId} onChange={(e) => setApprove((a) => ({ ...a, sprintId: e.target.value }))}>
                      <option value="">No specific sprint</option>
                      {sprints.map((sp) => (
                        <option key={sp._id} value={sp._id}>{sp.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {isFeature && (
                  <div>
                    <label className={label}>Parent epic</label>
                    {!approve.createParentEpic ? (
                      <>
                        <select className={input} value={approve.parentEpicId} onChange={(e) => setApprove((a) => ({ ...a, parentEpicId: e.target.value }))}>
                          <option value="">Select an epic…</option>
                          {epics.map((ep) => (
                            <option key={ep._id} value={ep._id}>{ep.displayId ? `${ep.displayId} — ` : ''}{ep.title}</option>
                          ))}
                        </select>
                        <button className="text-xs text-accent mt-1 hover:underline" onClick={() => setApprove((a) => ({ ...a, createParentEpic: true }))}>
                          + Create a new parent epic instead
                        </button>
                      </>
                    ) : (
                      <>
                        <input className={input} placeholder="New epic title" value={approve.newEpicTitle} onChange={(e) => setApprove((a) => ({ ...a, newEpicTitle: e.target.value }))} />
                        <button className="text-xs text-accent mt-1 hover:underline" onClick={() => setApprove((a) => ({ ...a, createParentEpic: false }))}>
                          Use an existing epic instead
                        </button>
                      </>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <button onClick={() => setApproving(false)} className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg">Cancel</button>
                  <button onClick={doApprove} disabled={busy} className="px-4 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg disabled:opacity-40">
                    Confirm approval
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer actions */}
          {!approving && idea.ideaStatus !== 'realized' && (
            <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap gap-2">
                {(NEXT_STATES[(idea.ideaStatus || 'draft') as IdeaStatus] || []).map((to) => (
                  <button
                    key={to}
                    onClick={() => transition(to)}
                    disabled={busy}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
                  >
                    {to === 'rejected' ? 'Reject' : to === 'parked' ? 'Park' : IDEA_STATUS_META[to].label}
                  </button>
                ))}
              </div>
              {canApprove && (
                <button
                  onClick={() => setApproving(true)}
                  disabled={busy}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg disabled:opacity-40"
                >
                  Approve <ArrowRight size={16} />
                </button>
              )}
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
